import React, { useState, useEffect } from "react";
import { FiMinus } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import { FaAngleDown } from "react-icons/fa";
import greeks from "greeks";
import { GrCircleQuestion } from "react-icons/gr";
import { Tooltip as ReactTooltip } from "react-tooltip";

const TargetSlidersMobile = ({
  expiries,
  daysleft,
  handleDateClick,
  selectedDate,
  isParamsValid,
  script,
  setLtpLine,
  handleLtpLineChange,
  initialLt,
  lowerBand1Value,
  upperBand1Value,
  lowerBand2Value,
  upperBand2Value,
  greeksData,
  setGreeksData,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialLtp = parseInt(initialLt) || 0;
  const [ltp, setLtp] = useState(initialLtp);
  const [percentage, setPercentage] = useState(0.0);
  const [isOpen, setIsOpen] = useState(false);
  const [sliderDate, setSliderDate] = useState(
    expiries && expiries.length > 0 ? expiries[0] : ""
  );
  const [sliderIndex, setSliderIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0: Target, 1: Strikewise IV's
  const [offsetIv, setOffsetIv] = useState(0);
  const [resetData, setResetData] = useState("");

  useEffect(() => {
    setResetData(greeksData);
  }, []);

  useEffect(() => {
    handleDateClick(sliderDate);
  }, [sliderDate, percentage]);

  useEffect(() => {
    setSliderDate(expiries[expiries.length - 1]);
  }, [expiries]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = daysOfWeek[date.getDay()];
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "short" });
    const year = date.toLocaleDateString(undefined, { year: "2-digit" });

    return `${day} ${month} ${year}`;
  };

  const handleResetDate = () => {
    setSliderDate(expiries[expiries.length - 1]);
    setSliderIndex(expiries[expiries.length - 1]);
  };

  const handleTargetReset = () => {
    setLtp(initialLtp);
    setPercentage(0.0);
    setLtpLine(initialLtp.toFixed(2));
    handleLtpLineChange(initialLtp.toFixed(2));
  };
  const handleStrikeReset = () => {
    setGreeksData(resetData);
    setOffsetIv(0);
  };

  const incrementLtp = () => {
    if (percentage < 5.0) {
      const newLtp = ltp + ltp * 0.005;
      setLtp((prevLtp) => Math.min(newLtp, initialLtp * 1.05));
      setPercentage((prevPercentage) => Math.min(prevPercentage + 0.5, 5.0));
      setLtpLine(newLtp.toFixed(2));
      handleLtpLineChange(newLtp.toFixed(2));
    }
  };

  const decrementLtp = () => {
    if (percentage > -5.0) {
      const newLtp = ltp - ltp * 0.005;
      setLtp((prevLtp) => Math.max(newLtp, initialLtp * 0.95));
      setPercentage((prevPercentage) => Math.max(prevPercentage - 0.5, -5.0));
      setLtpLine(newLtp.toFixed(2));
      handleLtpLineChange(newLtp.toFixed(2));
    }
  };

  const handleSliderChange = (event) => {
    const newLtp = parseFloat(event.target.value);
    const newPercentage = ((newLtp - initialLtp) / initialLtp) * 100;
    setLtp(newLtp);
    setPercentage(newPercentage);
    setLtpLine(newLtp.toFixed(2));
    handleLtpLineChange(newLtp.toFixed(2));
    // console.log("new ltp", newLtp.toFixed(2));
  };

  const handleDateSliderChange = (event) => {
    const selectedIndex = parseInt(event.target.value, 10);
    const selectedDate = expiries[selectedIndex];
    setSliderDate(selectedDate);
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return "";
    // Split the date string and return the first two parts (day and month)
    const [day, month] = dateStr.split(" ");
    return `${day} ${month}`;
  };
  // Increment all IVs globally
  const incrementOffsetIv = () => {
    setOffsetIv((prevOffset) => {
      const newOffset = prevOffset + 1;
      const updatedGreeksData = greeksData.map((option) => ({
        ...option,
        iv: (parseFloat(option.iv) + 1).toFixed(2), // Increment IV by 1
      }));
      setGreeksData(updatedGreeksData);
      computeGreeks(updatedGreeksData);
      return newOffset;
    });
  };

  // Decrement all IVs globally
  const decrementOffsetIv = () => {
    setOffsetIv((prevOffset) => {
      if (prevOffset > 0) {
        const newOffset = prevOffset - 1;
        const updatedGreeksData = greeksData.map((option) => ({
          ...option,
          iv: (parseFloat(option.iv) - 1).toFixed(2), // Decrement IV by 1
        }));
        setGreeksData(updatedGreeksData);
        computeGreeks(updatedGreeksData);
        return newOffset;
      }
      return prevOffset;
    });
  };

  // Function to increment/decrement IV for a specific strike (both call and put)
  const updateIndividualIv = (strike, price, change) => {
    const updatedGreeksData = greeksData.map((option) =>
      option.strike === strike && option.price === price
        ? {
          ...option,
          iv: (parseFloat(option.iv) + change).toFixed(2), // Increment or decrement IV based on `change`
        }
        : option
    );
    setGreeksData(updatedGreeksData);
    computeGreeks(updatedGreeksData);
  };

  const computeGreeks = (data) => {
    const riskFreeRate = 0.064515;

    // Construct updated greeksData with computed values
    const updatedGreeksData = data.map((option) => {
      // Determine if the option is a call or put based on the `isCALL` property
      const optionType = option.isCALL ? "call" : "put";

      // Calculate time to expiration
      const targetDateTimeString = option.expiry + " 15:29:59";
      const targetDateTime = new Date(targetDateTimeString);
      const currentDate = new Date();
      let timeToExpiration = Math.max(
        0,
        (targetDateTime - currentDate) / (24 * 60 * 60 * 365 * 1000) ||
        0.0002 / 365
      );

      // Use existing IV if available, or calculate it
      const initialGuessIv = option.iv / 100;

      // Only calculate Greeks if IV is less than or equal to 1 (assumes IV is a percentage)
      if (initialGuessIv <= 1) {
        const ivValue = (initialGuessIv * 100).toFixed(2);
        const deltaValue = (
          greeks.getDelta(
            ltp,
            option.strike,
            timeToExpiration,
            initialGuessIv,
            riskFreeRate,
            optionType
          )
        ).toFixed(2);
        const thetaValue = (
          greeks.getTheta(
            ltp,
            option.strike,
            timeToExpiration,
            initialGuessIv,
            riskFreeRate,
            optionType
          )
        ).toFixed(2);
        const gammaValue = (
          greeks.getGamma(
            ltp,
            option.strike,
            timeToExpiration,
            initialGuessIv,
            riskFreeRate,
            optionType
          )
        ).toFixed(4);
        const vegaValue = (
          greeks.getVega(
            ltp,
            option.strike,
            timeToExpiration,
            initialGuessIv,
            riskFreeRate,
            optionType
          )
        ).toFixed(2);
        const rhoValue = (
          greeks.getRho(
            ltp,
            option.strike,
            timeToExpiration,
            initialGuessIv,
            riskFreeRate,
            optionType
          )
        ).toFixed(2);

        // Return a new object with updated Greek values
        return {
          ...option,
          iv: ivValue,
          delta: deltaValue,
          theta: thetaValue,
          gamma: gammaValue,
          vega: vegaValue,
          rho: rhoValue,
        };
      }

      // If IV is greater than 1, return the option as is
      return option;
    });

    // console.log(updatedGreeksData, "updated greeks");
    // Set updated greeksData
    setGreeksData(updatedGreeksData);
  };

  return (
    <>
      {isParamsValid ? (
        <>
          <div className="w-[100%] h-auto flex flex-col  rounded-md mt-2 pb-5">
            {/* Tabs */}
            <div
              className="flex flex-row justify-between w-full border-2 border-n200 bg-p400 rounded-md p-0.5 mb-4"
              style={{ border: "2px solid rgba(156, 163, 175, 0.2)" }}>
              <div
                className={`text-[12px] py-2 px-4 cursor-pointer rounded-md w-[48%] text-center ${activeTab === 0
                  ? " bg-n200 text-n900 font-semibold bg-opacity-50"
                  : " text-n500 font-medium"
                  }`}
                onClick={() => setActiveTab(0)}>
                Target's
              </div>
              <div
                className={`text-[12px] py-2 px-4 cursor-pointer rounded-md w-[48%] text-center  ${activeTab === 1
                  ? " bg-n200 text-n900 font-semibold bg-opacity-50"
                  : " text-n500 font-medium"
                  }`}
                onClick={() => setActiveTab(1)}>
                Strikewise IV's
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 0 ? (
              <div
                className="border-2  rounded-md bg-p400"
                style={{ border: "2px solid rgba(156, 163, 175, 0.2)" }}>
                {/* First tab content */}
                <div className="flex flex-col w-[100%] h-fit py-[20px] px-[24px]">
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-col gap-[4px]">
                      <div className="text-p300 leading-[14px] w-auto font-medium text-[14px]">
                        <span>{script}</span> Target
                      </div>
                      <div
                        className="text-red w-fit leading-[14px] font-medium text-[14px] cursor-pointer"
                        onClick={handleTargetReset}>
                        Reset
                      </div>
                    </div>
                    <div className="flex flex-row gap-[12px] items-center ">
                      <div className="text-n700 leading-[14px] text-[14px] font-medium">
                        {percentage.toFixed(1)}%
                      </div>
                      <div
                        className="h-[34px] flex flex-row border-n000 border-2 rounded-md text-center items-center justify-between pr-[16px] pl-[16px] pt-[10px] pb-[10px] gap-[5px]"
                        style={{ width: "fixed" }}>
                        <div
                          role="button"
                          onClick={decrementLtp}
                          className="flex justify-center items-center h-[12px] w-[12px]">
                          <FiMinus className="text-n600 text-[10px] text-center" />
                        </div>
                        <span className="text-[14px] text-n900 font-medium leading-[14px] w-[66px] text-center items-center">
                          {ltp.toFixed(2)}
                        </span>
                        <div
                          role="button"
                          onClick={incrementLtp}
                          className="flex justify-center items-center h-[12px] w-[12px]">
                          <FaPlus className="text-n600 text-[10px] text-center" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* slider */}
                  <div className="slider">
                    <input
                      type="range"
                      min={initialLtp * 0.95} // Minimum value with 5% decrease
                      max={initialLtp * 1.05} // Maximum value with 5% increase
                      value={ltp}
                      step="0.5" // Ensure fine adjustments
                      onChange={handleSliderChange}
                    />
                  </div>
                </div>
                <div className="w-[100%] h-[2px] bg-n100 mt-1"></div>
                <div className="flex flex-col w-full h-fit  py-[20px] px-[24px]">
                  <div className="flex flex-row justify-between ">
                    <div className="flex flex-col gap-[4px] ">
                      <div className="text-p300 leading-[14px] font-medium text-[14px]">
                        Target Date{" "}
                        <span className="text-n500 text-[13px] font-normal">
                          ({daysleft} day{daysleft > 1 ? "s" : ""} to expire)
                        </span>
                      </div>
                      <div
                        className="text-red leading-[14px] font-medium text-[14px] cursor-pointer"
                        onClick={handleResetDate}>
                        Reset
                      </div>
                    </div>
                    <div className="cursor-pointer w-[110] h-fit pr-[16px] pl-[16px] py-[8px] flex flex-row justify-center items-center text-center gap-[8px] border border-n200  rounded-md relative">
                      <div onClick={() => {
                        setIsOpen(!isOpen);
                      }} className="text-n900 w-[61px] leading-[12px] text-[12px] font-semibold">
                        {formatDate(sliderDate)}
                      </div>
                      <FaAngleDown
                        className="text-n400 cursor-pointer"
                        onClick={() => {
                          setIsOpen(!isOpen);
                        }}
                      />
                      {isOpen && (
                        <ul
                          className={`transition-all duration-300 ease-in-out transform absolute top-[36px] mt-1 bg-p400 border no-scrollbar border-n200 w-[117px] rounded-md shadow-lg z-10 overflow-y-auto max-h-[100px] ${isOpen
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-95"
                            }`}>
                          {expiries?.map((o, ind) => (
                            <li
                              key={ind}
                              className="px-4 py-2 cursor-pointer hover:bg-n100 text-n900 text-[12px] font-medium"
                              onClick={() => {
                                handleDateClick(o);
                                setSliderDate(o);
                                setIsOpen(false);
                              }}>
                              {formatDate(o)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="slider pb-4">
                    <input
                      type="range"
                      min={0}
                      max={expiries.length - 1}
                      value={expiries.findIndex(
                        (expiry) => expiry === sliderDate
                      )}
                      step="1"
                      onChange={handleDateSliderChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Second tab content */}
                <div
                  className="border-2  rounded-md bg-p400"
                  style={{ border: "2px solid rgba(156, 163, 175, 0.2)" }}>
                  <div className="flex flex-col w-[100%] h-fit py-[20px] px-[24px]">
                    <div className="flex flex-row justify-between">
                      <div className="flex flex-col gap-[4px]">
                        <div className="text-p300 leading-[14px] w-auto font-medium text-[14px]">
                          Strikewise IV's
                        </div>
                        <div
                          className="text-red w-fit leading-[14px] font-medium text-[14px] cursor-pointer"
                          onClick={handleStrikeReset}>
                          Reset
                        </div>
                      </div>
                      <div className="flex flex-row gap-[12px] items-center ">
                        <div>
                          <div className="text-n700 leading-[14px] text-[14px] font-medium">
                            Off
                          </div>
                          <div className="text-n700 leading-[14px] text-[14px] font-medium">
                            set
                          </div>
                        </div>
                        <div
                          className="h-[34px] flex flex-row border-n000 border-2 rounded-md text-center items-center justify-between pr-[16px] pl-[16px] pt-[10px] pb-[10px] gap-[5px]"
                          style={{ width: "fixed" }}>
                          <div
                            role="button"
                            onClick={decrementOffsetIv}
                            className="flex justify-center items-center h-[12px] w-[12px]">
                            <FiMinus className="text-n600 text-[10px] text-center" />
                          </div>
                          <span className="text-[14px] text-n900 font-medium leading-[14px] w-[66px] text-center items-center">
                            {offsetIv}
                          </span>
                          <div
                            role="button"
                            onClick={incrementOffsetIv}
                            className="flex justify-center items-center h-[12px] w-[12px]">
                            <FaPlus className="text-n600 text-[10px] text-center" />
                          </div>
                        </div>
                      </div>
                    </div>
                    {greeksData.map((greek) => {
                      return (
                        <div className="flex flex-row justify-between pt-4">
                          <div className=" flex flex-row gap-2 items-center">
                            <div className=" text-n700 font-medium text-[14px]">
                              {greek.strike}
                            </div>
                            <GrCircleQuestion
                              color="grey"
                              width={16}
                              height={16}
                              data-tooltip-id={`tooltip-${greek.strike}`}
                            />
                            <ReactTooltip
                              id={`tooltip-${greek.strike}`}
                              place="top"
                              effect="solid"
                              content={`The IV on the target date of ${greek.strike}`}
                              className="w-80"
                            />
                          </div>

                          <div className="flex flex-row gap-[12px] items-center ">
                            <div className="text-n700 leading-[14px] text-[14px] font-medium">
                              {formatExpiryDate(greek.expiry)}
                            </div>
                            <div
                              className="h-[34px] flex flex-row border-n000 border-2 rounded-md text-center items-center justify-between pr-[16px] pl-[16px] pt-[10px] pb-[10px] gap-[5px]"
                              style={{ width: "fixed" }}>
                              <div
                                role="button"
                                onClick={() =>
                                  updateIndividualIv(
                                    greek.strike,
                                    greek.price,
                                    -1
                                  )
                                }
                                className="flex justify-center items-center h-[12px] w-[12px]">
                                <FiMinus className="text-n600 text-[10px] text-center" />
                              </div>
                              <span className="text-[14px] text-n900 font-medium leading-[14px] w-[66px] text-center items-center">
                                {parseFloat(greek.iv)}
                              </span>
                              <div
                                role="button"
                                onClick={() =>
                                  updateIndividualIv(
                                    greek.strike,
                                    greek.price,
                                    1
                                  )
                                }
                                className="flex justify-center items-center h-[12px] w-[12px]">
                                <FaPlus className="text-n600 text-[10px] text-center" />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="w-[100%] h-[2px] bg-n100 mt-1"></div>
                  <div className="flex flex-col w-full h-fit  py-[20px] px-[24px]">
                    <div className="flex gap-2 items-center pb-4">
                      <div className="text-p300 leading-[14px] font-medium text-[14px]">
                        Standard Deviations{" "}
                      </div>
                      <GrCircleQuestion
                        color="grey"
                        width={16}
                        height={16}
                        data-tooltip-id="tooltip-std"
                      />
                      <ReactTooltip
                        id="tooltip-std"
                        place="top"
                        effect="solid"
                        content={
                          "Helps determine the spread of asset prices from their average price."
                        }
                        style={{ maxWidth: "20rem" }}
                      />
                    </div>
                    <div class="container mx-auto">
                      <div class="grid grid-cols-3 gap-4 text-center">
                        <div className=" grid grid-rows-3 text-start gap-2 items-center">
                          <div></div>
                          <div className="font-medium text-[12px] text-n500">
                            1 SD
                          </div>
                          <div className="font-medium text-[12px] text-n500">
                            2 SD
                          </div>
                        </div>

                        <div className=" grid grid-rows-3 text-start items-center">
                          <div className="font-medium text-[12px] text-n500">
                            Points
                          </div>
                          <div className="font-medium text-[12px] text-n600">
                            {(upperBand1Value - lowerBand1Value).toFixed(2)} (
                            {(
                              ((upperBand1Value - lowerBand1Value) / ltp) *
                              100
                            ).toFixed(2)}
                            %)
                          </div>
                          <div className="font-medium text-[12px] text-n600">
                            {(upperBand2Value - lowerBand2Value).toFixed(2)} (
                            {(
                              ((upperBand2Value - lowerBand2Value) / ltp) *
                              100
                            ).toFixed(2)}
                            %)
                          </div>
                        </div>

                        <div className=" grid grid-rows-3 text-start items-center">
                          <div className="font-medium text-[12px] text-n500">
                            Price
                          </div>
                          <div className="font-medium text-[12px] text-n600">
                            {lowerBand1Value} - {upperBand1Value}
                          </div>
                          <div className="font-medium text-[12px] text-n600">
                            {lowerBand2Value} - {upperBand2Value}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : null}
    </>
  );
};

export default TargetSlidersMobile;
